import { AiComponentCompileError } from '@Pages/pageRuntime/compiler/AiComponentCompileError';
import {
  evaluateTransformedComponent,
  type CompiledAiComponent,
  type PageRuntimeSourceKind,
  type TransformedPageSourceWithDiagnostics
} from '@Pages/pageRuntime/compiler/compileAiComponent';
import {
  EditorTransformSupersededError,
  getEditorTransformClient,
  type EditorTransformClient
} from '@Pages/pageRuntime/compiler/editorTransformClient';
import {
  diagnosticLocation,
  type EditorTransformDiagnostic,
  type EditorTransformSource
} from '@Pages/pageRuntime/compiler/editorTransformProtocol';
import { pageSourceUrl } from '@Pages/pageRuntime/compiler/pageSourceDiagnostics';
import {
  pageSdkErrorTelemetryMode,
  reportPageSdkError
} from '@Pages/pageRuntime/compiler/reportPageSdkError';
import { applyPreviewStylesheet } from '@Pages/pageRuntime/compiler/tailwind/applyPreviewStylesheet';
import { componentSourcePath } from '@Pages/pageRuntime/designMode/sourceMetadata';
import {
  evaluationRuntimeLoads,
  optionalRuntimeLoads
} from '@Pages/pageRuntime/prepareOptionalRuntime';
import type { SharedComponentImplementations } from '@Pages/pageRuntime/sharedComponentForwarding';
import { withOptionalRuntime } from '@Pages/pageRuntime/withOptionalRuntime';
import { useEffect, useState } from 'react';

export type EditorPreviewStatus = 'initializing' | 'ready' | 'stale';

export type PageRuntimeRootKind = Exclude<PageRuntimeSourceKind, 'shared'>;

export type CommittedRootSource = {
  code: string;
  compiled: CompiledAiComponent;
};

export type AuthoringRuntime = {
  status: EditorPreviewStatus;
  diagnostic: EditorTransformDiagnostic | null;
  /**
   * The evaluation error behind `diagnostic`, kept so the error the renderer
   * reports retains its cause chain. Null when the diagnostic came from the
   * transform, which never has an error object.
   */
  diagnosticError: AiComponentCompileError | null;
  /** Whether any generation has ever committed, so something can render. */
  hasCommit: boolean;
  /**
   * The root that owns the diagnostic of a first generation that never
   * committed, so exactly one renderer shows the compile fallback.
   */
  failedRootKey: string | null;
  /**
   * Whether the failed root has no committed version at all. Its renderer then
   * shows the compile fallback, and there is no "last version that built" for
   * a notice to point at.
   */
  isFailedRootUnbuilt: boolean;
  registerRootSource(kind: PageRuntimeRootKind, sourcePath: string, code: string | null): void;
  releaseRootSource(kind: PageRuntimeRootKind, sourcePath: string): void;
  committedRoot(kind: PageRuntimeRootKind, sourcePath: string): CommittedRootSource | null;
};

type SharedComponentInput = { name: string; code: string };

type RootSource = {
  kind: PageRuntimeRootKind;
  sourcePath: string;
  code: string | null;
};

/** A transformed source plus everything needed to evaluate it again. */
type EvaluableSource = {
  key: string;
  kind: PageRuntimeSourceKind;
  sourcePath: string;
  name: string | null;
  code: string;
  transformed: TransformedPageSourceWithDiagnostics;
  cacheToken: number | null;
};

/** Shared components that did not transform, by name, so a commit can carry them. */
type SharedFailures = Map<string, EditorTransformDiagnostic>;

type Commit = {
  generation: number;
  sdk: unknown;
  sources: EvaluableSource[];
  sharedFailures: SharedFailures;
  roots: Map<string, CommittedRootSource>;
  implementations: SharedComponentImplementations;
};

type GenerationState = {
  status: EditorPreviewStatus;
  diagnostic: EditorTransformDiagnostic | null;
  diagnosticError: AiComponentCompileError | null;
  failedRootKey: string | null;
  sourceFailure: {
    diagnostic: EditorTransformDiagnostic;
    error: AiComponentCompileError | null;
    failedRootKey: string | null;
  } | null;
  commit: Commit | null;
  /** The most recent `Sdk` a re-evaluation was attempted against. */
  attemptedSdk: unknown;
};

type GenerationInputs = {
  Sdk: unknown;
  authoring: boolean;
  enabled: boolean;
  /** Whether this document's CSS is the worker's to compile and mount. */
  stylesheet: boolean;
  sharedComponents: SharedComponentInput[];
};

const NO_SHARED_COMPONENTS: SharedComponentInput[] = [];

const INITIAL_STATE: GenerationState = {
  status: 'initializing',
  diagnostic: null,
  diagnosticError: null,
  failedRootKey: null,
  sourceFailure: null,
  commit: null,
  attemptedSdk: null
};

export function pageRuntimeRootKey(kind: PageRuntimeRootKind, sourcePath: string): string {
  return `${kind} ${sourcePath}`;
}

function compileErrorDiagnostic(
  error: AiComponentCompileError,
  source: EvaluableSource
): EditorTransformDiagnostic {
  return {
    message: error.message,
    sourcePath: error.sourcePath ?? source.sourcePath,
    sourceLabel: error.sourceLabel ?? pageSourceUrl(source.sourcePath),
    line: error.line,
    column: error.column
  };
}

type EvaluatedSource = {
  sdk: unknown;
  codeHash: string | undefined;
  compiled: CompiledAiComponent;
  implementation: React.ComponentType | null;
};

/**
 * The error a page meets where it renders a shared component that did not
 * build. It throws from the forwarding component into the section's error
 * boundary, so the failure stays where the component is used — the same reach
 * a published page gives it.
 */
function sharedComponentError(
  name: string,
  diagnostic: EditorTransformDiagnostic,
  cause?: Error
): AiComponentCompileError {
  const location = diagnosticLocation(diagnostic);
  const message = location ? `${diagnostic.message} (${location})` : diagnostic.message;

  return new AiComponentCompileError(`Sdk.Custom.${name} failed to compile: ${message}`, {
    ...(cause ? { cause } : {}),
    sourcePath: diagnostic.sourcePath,
    sourceLabel: diagnostic.sourceLabel,
    line: diagnostic.line,
    column: diagnostic.column
  });
}

/**
 * Evaluates every source of a generation against one `Sdk`.
 *
 * The roots are all or none: a preview built from a page that saw the new
 * `Sdk` and a layout that saw the old one is not a state the author ever wrote.
 * A shared component that fails is different. It becomes an error where it is
 * used and nowhere else, because one component out of a whole catalog must not
 * take the preview away from a page that never renders it.
 *
 * A source the author did not touch is reused from `evaluated` rather than run
 * through `new Function` again. That saves the work, but the reason it matters
 * is identity: a page reaches a shared component through `Sdk.Custom`, which
 * renders whatever `implementations` holds, so a fresh component type for an
 * untouched component would unmount its subtree on every keystroke.
 */
function evaluateGeneration(
  sources: EvaluableSource[],
  Sdk: unknown,
  authoring: boolean,
  evaluated: Map<string, EvaluatedSource>,
  sharedFailures: SharedFailures
):
  | {
      ok: true;
      roots: Map<string, CommittedRootSource>;
      implementations: SharedComponentImplementations;
    }
  | { ok: false; diagnostic: EditorTransformDiagnostic; error: AiComponentCompileError } {
  const roots = new Map<string, CommittedRootSource>();
  const implementations: SharedComponentImplementations = {};
  const reached = new Set<string>();

  for (const [name, diagnostic] of sharedFailures) {
    implementations[name] = sharedComponentError(name, diagnostic);
  }

  for (const source of sources) {
    reached.add(source.key);
    const reusable = evaluated.get(source.key);
    let entry: EvaluatedSource;

    if (
      reusable &&
      reusable.sdk === Sdk &&
      reusable.codeHash !== undefined &&
      reusable.codeHash === source.transformed.codeHash
    ) {
      entry = reusable;
    } else {
      const compiled = evaluateTransformedComponent(source.transformed, Sdk, {
        authoring,
        kind: source.kind,
        sourceBytes: new Blob([source.code]).size
      });

      if (compiled.error) {
        const diagnostic = compileErrorDiagnostic(compiled.error, source);

        if (source.kind === 'shared' && source.name) {
          evaluated.delete(source.key);
          implementations[source.name] = sharedComponentError(
            source.name,
            diagnostic,
            compiled.error
          );

          continue;
        }

        return { ok: false, diagnostic, error: compiled.error };
      }

      entry = {
        sdk: Sdk,
        codeHash: source.transformed.codeHash,
        compiled,
        implementation:
          source.kind === 'shared' && compiled.Component
            ? withOptionalRuntime(compiled.Component, source.code)
            : null
      };
      evaluated.set(source.key, entry);
    }

    if (source.kind === 'shared') {
      if (source.name && entry.implementation) {
        implementations[source.name] = entry.implementation;
      }

      continue;
    }

    roots.set(pageRuntimeRootKey(source.kind, source.sourcePath), {
      code: source.code,
      compiled: entry.compiled
    });
  }

  for (const key of evaluated.keys()) {
    if (!reached.has(key)) {
      evaluated.delete(key);
    }
  }

  return { ok: true, roots, implementations };
}

/**
 * A root with no code still commits, as the empty state rather than as nothing —
 * otherwise an empty page would look like a preview that never finished.
 */
function withEmptyRoots(
  roots: Map<string, CommittedRootSource>,
  registered: Map<string, RootSource>
): Map<string, CommittedRootSource> {
  const committed = new Map(roots);

  for (const [key, root] of registered) {
    if (!committed.has(key) && !root.code?.trim()) {
      committed.set(key, { code: root.code ?? '', compiled: { Component: null, error: null } });
    }
  }

  return committed;
}

function failedRootKeyFor(
  diagnostic: EditorTransformDiagnostic,
  registered: Map<string, RootSource>
): string | null {
  for (const [key, root] of registered) {
    if (root.sourcePath === diagnostic.sourcePath) {
      return key;
    }
  }

  // A shared component has no renderer of its own, so the page reports for it —
  // and a preview without a page falls back to whichever root registered first.
  for (const [key, root] of registered) {
    if (root.kind === 'page') {
      return key;
    }
  }

  return registered.keys().next().value ?? null;
}

type GenerationEngine = {
  setInputs(inputs: GenerationInputs): void;
  schedule(): void;
  reevaluate(Sdk: unknown, authoring: boolean): void;
  warm(): void;
  resume(): void;
  register(kind: PageRuntimeRootKind, sourcePath: string, code: string | null): void;
  release(kind: PageRuntimeRootKind, sourcePath: string): void;
  dispose(): void;
};

/**
 * Owns the roots the preview has registered and turns any change to them into
 * exactly one transform generation per microtask, so a page, its layout and
 * their shared components are always transformed together.
 */
function createGenerationEngine({
  inputs: initialInputs,
  setState
}: {
  inputs: GenerationInputs;
  setState: (update: (current: GenerationState) => GenerationState) => void;
}): GenerationEngine {
  let inputs = initialInputs;
  const roots = new Map<string, RootSource>();
  const evaluated = new Map<string, EvaluatedSource>();
  const transformedSources = new Map<string, EvaluableSource>();
  let transformClient: EditorTransformClient | null = null;
  let requestCounter = 0;
  let isScheduled = false;
  let isDisposed = false;
  let hasRestarted = false;
  let hasCommitted = false;

  function resolveClient(): EditorTransformClient {
    transformClient ??= getEditorTransformClient();

    return transformClient;
  }

  function requestSources(): Array<EditorTransformSource & { name: string | null }> {
    const { authoring, sharedComponents } = inputs;
    const sources: Array<EditorTransformSource & { name: string | null }> = [];

    function addSource(
      key: string,
      kind: PageRuntimeSourceKind,
      sourcePath: string,
      code: string,
      name: string | null
    ): void {
      const transformed = transformedSources.get(key);
      const base = { key, kind, sourcePath, authoring, name };

      sources.push(
        transformed?.code === code && transformed.cacheToken !== null
          ? { ...base, cacheToken: transformed.cacheToken }
          : { ...base, code }
      );
    }

    for (const root of roots.values()) {
      if (root.code?.trim()) {
        addSource(
          pageRuntimeRootKey(root.kind, root.sourcePath),
          root.kind,
          root.sourcePath,
          root.code,
          null
        );
      }
    }

    for (const component of sharedComponents) {
      if (component.code.trim()) {
        addSource(
          `shared ${component.name}`,
          'shared',
          componentSourcePath(component.name),
          component.code,
          component.name
        );
      }
    }

    return sources;
  }

  /**
   * A stylesheet the worker could not compile fails the generation the same way
   * a source that did not transform does: the last generation that built keeps
   * its code *and* its CSS mounted, and the author is told why. Anything else
   * would leave the preview showing new markup styled by an old stylesheet.
   */
  function failStylesheetGeneration(message: string): void {
    const error = new Error(`Preview styles failed to compile. ${message}`.trim());

    reportPageSdkError(error, {
      kind: 'compile',
      source: 'page',
      renderSurface: inputs.authoring ? 'editor-preview' : 'draft-preview',
      telemetryMode: pageSdkErrorTelemetryMode(
        error,
        inputs.authoring ? 'editor-preview' : 'draft-preview'
      )
    });

    failGeneration({
      message: error.message,
      sourcePath: '',
      sourceLabel: '',
      line: null,
      column: null
    });
  }

  function failGeneration(
    diagnostic: EditorTransformDiagnostic,
    error: AiComponentCompileError | null = null
  ): void {
    // Named on every failure, not only before the first commit: a layout can
    // commit while the page inside it has never built, and that page still has
    // to show the compile fallback rather than an empty content area. Only a
    // root without a commit of its own reads this, and it names one root, so it
    // cannot raise two fallbacks.
    const failedRootKey = failedRootKeyFor(diagnostic, roots);

    setState((current) => ({
      ...current,
      status: 'stale',
      diagnostic,
      diagnosticError: error,
      failedRootKey,
      sourceFailure: { diagnostic, error, failedRootKey }
    }));
  }

  async function run(): Promise<void> {
    const requestId = (requestCounter += 1);
    const { authoring } = inputs;
    const sources = requestSources();
    const isCurrent = () => requestId === requestCounter && !isDisposed;
    let response;

    try {
      response = await resolveClient().transform(sources, { stylesheet: inputs.stylesheet });
    } catch (error) {
      // A superseded call was replaced by a newer one that is already running,
      // so the preview waits for that one rather than reacting to this.
      if (error instanceof EditorTransformSupersededError || !isCurrent()) {
        return;
      }

      failGeneration({
        message:
          `The preview compiler restarted. ${error instanceof Error ? error.message : ''}`.trim(),
        sourcePath: '',
        sourceLabel: '',
        line: null,
        column: null
      });

      if (!hasRestarted) {
        hasRestarted = true;
        schedule();
      }

      return;
    }

    if (!isCurrent()) {
      return;
    }

    const cacheMisses = response.results.filter(
      (result) => !result.ok && result.cacheMiss === true
    );

    if (cacheMisses.length > 0) {
      for (const result of cacheMisses) {
        transformedSources.delete(result.key);
      }

      schedule();

      return;
    }

    const requested = new Map(sources.map((source) => [source.key, source]));
    const evaluable: EvaluableSource[] = [];

    for (const result of response.results) {
      const source = result.ok ? requested.get(result.key) : undefined;

      if (!result.ok || !source) {
        continue;
      }

      if (result.referenced) {
        const transformed = transformedSources.get(result.key);

        if (!transformed || transformed.cacheToken !== result.cacheToken) {
          transformedSources.delete(result.key);
          schedule();

          return;
        }

        evaluable.push(transformed);

        continue;
      }

      if (typeof source.code !== 'string') {
        transformedSources.delete(result.key);
        schedule();

        return;
      }

      const transformed: EvaluableSource = {
        key: source.key,
        kind: source.kind,
        sourcePath: source.sourcePath,
        name: source.name,
        code: source.code,
        transformed: {
          code: result.code,
          sourcePath: source.sourcePath,
          sourceLabel: result.sourceLabel,
          sourceMap: result.sourceMap,
          codeHash: result.codeHash
        },
        cacheToken: result.cacheToken
      };
      evaluable.push(transformed);
      transformedSources.set(source.key, transformed);
    }

    // A root that does not transform fails the generation. A shared component
    // that does not transform only fails where it is used.
    const sharedFailures: SharedFailures = new Map();

    for (const result of response.results) {
      if (result.ok || result.cacheMiss === true) {
        continue;
      }

      const source = requested.get(result.key);

      if (source?.kind === 'shared' && source.name) {
        sharedFailures.set(source.name, result.diagnostic);

        continue;
      }

      failGeneration(result.diagnostic);

      return;
    }

    // Only after every source has transformed: a diagnostic that names a line
    // of the author's code is more use than one about the stylesheet built from
    // it, and a generation that has already failed never mounts either.
    if (response.stylesheet && !response.stylesheet.ok) {
      failStylesheetGeneration(response.stylesheet.message);

      return;
    }

    const reached = new Set(evaluable.map((source) => source.key));

    for (const key of transformedSources.keys()) {
      if (!reached.has(key)) {
        transformedSources.delete(key);
      }
    }

    const changedSources = evaluable.filter(
      (source) => evaluated.get(source.key)?.codeHash !== source.transformed.codeHash
    );
    // The first generation has no stale preview to preserve. Its shared
    // components load hooks and component chunks only if they render, while
    // DnD still loads before a source factory can read its proxy. Later edits
    // preload every dependency before replacing the last working generation.
    const fullRuntimeSources = hasCommitted
      ? changedSources
      : changedSources.filter((source) => source.kind !== 'shared');
    const evaluationOnlySources = hasCommitted
      ? []
      : changedSources.filter((source) => source.kind === 'shared');
    const loads = [
      optionalRuntimeLoads(fullRuntimeSources.map((source) => source.code)),
      evaluationRuntimeLoads(evaluationOnlySources.map((source) => source.code))
    ].filter((load): load is Promise<unknown> => load !== null);

    if (loads.length > 0) {
      try {
        await Promise.all(loads);
      } catch (error) {
        if (isCurrent()) {
          failGeneration({
            message:
              `The preview runtime could not load. ${error instanceof Error ? error.message : ''}`.trim(),
            sourcePath: '',
            sourceLabel: '',
            line: null,
            column: null
          });
        }

        return;
      }

      if (!isCurrent()) {
        return;
      }
    }

    const result = evaluateGeneration(evaluable, inputs.Sdk, authoring, evaluated, sharedFailures);

    if (!result.ok) {
      failGeneration(result.diagnostic, result.error);

      return;
    }

    hasRestarted = false;
    commitGeneration(
      response.generation,
      evaluable,
      sharedFailures,
      inputs.Sdk,
      result,
      response.stylesheet?.ok ? response.stylesheet.css : null
    );
  }

  function commitGeneration(
    generation: number,
    sources: EvaluableSource[],
    sharedFailures: SharedFailures,
    Sdk: unknown,
    result: {
      roots: Map<string, CommittedRootSource>;
      implementations: SharedComponentImplementations;
    },
    css: string | null
  ): void {
    hasCommitted = true;

    // Written in the same task as the state update that mounts the transformed
    // code, so the browser cannot paint between the two and no frame shows new
    // code with old CSS.
    if (css !== null) {
      applyPreviewStylesheet(css);
    }

    setState(() => ({
      status: 'ready',
      diagnostic: null,
      diagnosticError: null,
      failedRootKey: null,
      sourceFailure: null,
      attemptedSdk: Sdk,
      commit: {
        generation,
        sdk: Sdk,
        sources,
        sharedFailures,
        roots: withEmptyRoots(result.roots, roots),
        implementations: result.implementations
      }
    }));
  }

  /**
   * An `Sdk` change has to reach the page's top-level code, which closed over
   * the previous one. The transformed output is already in hand, so this is a
   * re-evaluation rather than another worker round trip.
   *
   * `attemptedSdk` records the attempt whether or not it worked, so a commit
   * that cannot be re-evaluated is not retried on every following render.
   */
  function reevaluate(Sdk: unknown, authoring: boolean): void {
    setState((current) => {
      if (!current.commit) {
        return current;
      }

      const result = evaluateGeneration(
        current.commit.sources,
        Sdk,
        authoring,
        evaluated,
        current.commit.sharedFailures
      );

      if (!result.ok) {
        return {
          ...current,
          status: 'stale',
          diagnostic: current.sourceFailure?.diagnostic ?? result.diagnostic,
          diagnosticError: current.sourceFailure ? current.sourceFailure.error : result.error,
          failedRootKey: current.sourceFailure?.failedRootKey ?? current.failedRootKey,
          attemptedSdk: Sdk
        };
      }

      return {
        status: current.sourceFailure ? 'stale' : 'ready',
        diagnostic: current.sourceFailure?.diagnostic ?? null,
        diagnosticError: current.sourceFailure?.error ?? null,
        failedRootKey: current.sourceFailure?.failedRootKey ?? null,
        sourceFailure: current.sourceFailure,
        attemptedSdk: Sdk,
        commit: {
          ...current.commit,
          sdk: Sdk,
          roots: withEmptyRoots(result.roots, roots),
          implementations: result.implementations
        }
      };
    });
  }

  function schedule(): void {
    if (isScheduled || !inputs.enabled) {
      return;
    }

    isScheduled = true;
    queueMicrotask(() => {
      isScheduled = false;

      if (!isDisposed) {
        void run();
      }
    });
  }

  return {
    setInputs(next) {
      inputs = next;
    },
    schedule,
    reevaluate,
    warm() {
      resolveClient().warm({ stylesheet: inputs.stylesheet });
    },
    resume() {
      isDisposed = false;
    },
    register(kind, sourcePath, code) {
      const key = pageRuntimeRootKey(kind, sourcePath);
      const existing = roots.get(key);

      // Always a fresh object, so a release queued by the same commit can tell a
      // re-registration from an unmount by identity.
      roots.set(key, { kind, sourcePath, code });

      if (!existing || existing.code !== code) {
        schedule();
      }
    },
    release(kind, sourcePath) {
      const key = pageRuntimeRootKey(kind, sourcePath);
      const released = roots.get(key);

      if (!released) {
        return;
      }

      // React runs an effect's cleanup and its replacement in the same commit,
      // so a source that is still mounted has re-registered before this runs.
      queueMicrotask(() => {
        if (roots.get(key) === released) {
          roots.delete(key);
          schedule();
        }
      });
    },
    dispose() {
      isDisposed = true;
    }
  };
}

/**
 * Drives the authoring preview's transform generations.
 *
 * Every registered root and every shared component travels in one generation, so
 * a commit swaps the whole preview at once — including the preview stylesheet
 * the worker compiles from the same sources, which is written to the document
 * in the same task as the code it styles. Until a generation both transforms
 * and evaluates cleanly the last committed generation keeps rendering — an
 * author who breaks a line keeps the working preview, its state, and the form
 * they had half filled in.
 */
export function useEditorPreviewGeneration({
  Sdk,
  sharedComponents,
  authoring,
  enabled,
  stylesheet
}: {
  Sdk: unknown;
  sharedComponents: SharedComponentInput[] | undefined;
  authoring: boolean;
  enabled: boolean;
  stylesheet: boolean;
}): { authoringRuntime: AuthoringRuntime; implementations: SharedComponentImplementations } {
  const [state, setState] = useState<GenerationState>(INITIAL_STATE);
  const components = sharedComponents ?? NO_SHARED_COMPONENTS;
  // The engine reads its inputs when a generation runs, not when it was built,
  // so a change to any of them does not have to rebuild it. It owns them
  // outright rather than through a ref, which keeps this render free of the ref
  // access that would stop React Compiler optimizing the provider.
  const [engine] = useState(() =>
    createGenerationEngine({
      inputs: { Sdk, authoring, enabled, stylesheet, sharedComponents: components },
      setState
    })
  );
  const sharedSignature = components
    .map((component) => `${component.name} ${component.code}`)
    .join('\u0000');

  // A generation runs in a microtask, which is after every effect of the commit
  // that scheduled it, so publishing the inputs here always reaches it in time.
  useEffect(() => {
    engine.setInputs({ Sdk, authoring, enabled, stylesheet, sharedComponents: components });
  });

  useEffect(() => {
    if (enabled) {
      engine.warm();
      engine.schedule();
    }
  }, [enabled, engine, sharedSignature]);

  useEffect(() => {
    engine.resume();

    return () => engine.dispose();
  }, [engine]);

  if (enabled && state.commit && state.commit.sdk !== Sdk && state.attemptedSdk !== Sdk) {
    engine.reevaluate(Sdk, authoring);
  }

  const authoringRuntime: AuthoringRuntime = {
    status: state.status,
    diagnostic: state.diagnostic,
    diagnosticError: state.diagnosticError,
    hasCommit: state.commit !== null,
    failedRootKey: state.failedRootKey,
    isFailedRootUnbuilt:
      state.failedRootKey !== null && !state.commit?.roots.has(state.failedRootKey),
    registerRootSource: engine.register,
    releaseRootSource: engine.release,
    committedRoot: (kind, sourcePath) =>
      state.commit?.roots.get(pageRuntimeRootKey(kind, sourcePath)) ?? null
  };

  return { authoringRuntime, implementations: state.commit?.implementations ?? {} };
}
